import { UIMessage } from 'ai';

export type AgentToolResult =
  | {
      kind: 'contact';
      action: 'created' | 'updated';
      contact: { id: string; displayName?: string; firstName?: string; lastName?: string };
    }
  | {
      kind: 'event';
      action: 'created' | 'updated' | 'deleted';
      event: { id: string; name: string; isRecurring?: boolean; scope?: 'occurrence' | 'series' };
    }
  | {
      kind: 'task';
      action: 'created' | 'updated' | 'deleted';
      task: { id: string; title: string; status?: string };
    }
  | { kind: 'document'; action: 'shown'; document: AgentDocument }
  | { kind: 'folder'; action: 'created'; folder: { path: string } }
  | {
      kind: 'documents';
      action: 'renamed' | 'moved';
      count: number;
      /** Folder holding them all, when there is one. */
      folder?: string;
    };

export interface AgentDocument {
  id: string;
  name: string;
  type: 'file' | 'folder' | 'video' | 'link';
  /** Folder holding the item. */
  folder: string;
  /** Full path, for a folder. */
  path?: string;
}

type BatchResult = { status: string; folder?: string };

/** Documents page in a folder, opening a file when given (read by DocumentBrowser). */
export const documentsLink = (
  assetId: string | undefined,
  { path, open }: { path?: string; open?: string }
) => {
  const params = new URLSearchParams();
  if (path) params.set('path', path);
  if (open) params.set('open', open);
  const query = params.toString();
  return `/asset/${assetId}/documents${query ? `?${query}` : ''}`;
};

/** "/Factures/Jardin/" -> "Factures/Jardin", the root -> "Documents". */
export const folderLabel = (path: string) =>
  path.split('/').filter(Boolean).join('/') || 'Documents';

type McpToolOutput = { isError?: boolean; content?: { type: string; text?: string }[] };

const WRITE_TOOLS: Record<
  string,
  { kind: AgentToolResult['kind']; action: 'created' | 'updated' | 'deleted' }
> = {
  create_contact: { kind: 'contact', action: 'created' },
  update_contact: { kind: 'contact', action: 'updated' },
  create_event: { kind: 'event', action: 'created' },
  update_event: { kind: 'event', action: 'updated' },
  delete_event: { kind: 'event', action: 'deleted' },
  create_task: { kind: 'task', action: 'created' },
  update_task: { kind: 'task', action: 'updated' },
  delete_task: { kind: 'task', action: 'deleted' },
};

/** Document tools report one result per item: one card sums up the batch. */
const batchResult = (
  action: 'renamed' | 'moved',
  { results }: { results: BatchResult[] }
): AgentToolResult | undefined => {
  const done = results.filter(result => result.status === action);
  if (!done.length) {
    return undefined;
  }
  const folders = [...new Set(done.map(result => result.folder))];
  return {
    kind: 'documents',
    action,
    count: done.length,
    folder: folders.length === 1 ? folders[0] : undefined,
  };
};

const DOCUMENT_TOOLS: Record<string, (output: unknown) => AgentToolResult | undefined> = {
  show_document: output => ({
    kind: 'document',
    action: 'shown',
    document: output as AgentDocument,
  }),
  create_folder: output => ({
    kind: 'folder',
    action: 'created',
    folder: { path: (output as { path: string }).path },
  }),
  rename_documents: output => batchResult('renamed', output as { results: BatchResult[] }),
  move_documents: output => batchResult('moved', output as { results: BatchResult[] }),
};

/** What a successful MCP tool call worth a card (contact, event, task, document) returned. */
export const getAgentToolResult = (
  part: UIMessage['parts'][number]
): AgentToolResult | undefined => {
  if (part.type !== 'dynamic-tool' || part.state !== 'output-available') {
    return undefined;
  }
  const tool = WRITE_TOOLS[part.toolName];
  const documentTool = DOCUMENT_TOOLS[part.toolName];
  if (!tool && !documentTool) {
    return undefined;
  }
  const output = part.output as McpToolOutput | undefined;
  if (output?.isError) {
    return undefined;
  }
  try {
    const entity = JSON.parse(output?.content?.[0]?.text ?? '');
    if (documentTool) {
      return documentTool(entity);
    }
    if (tool.kind === 'contact') {
      return { kind: 'contact', action: tool.action as 'created' | 'updated', contact: entity };
    }
    return tool.kind === 'task'
      ? { kind: 'task', action: tool.action, task: entity }
      : { kind: 'event', action: tool.action, event: entity };
  } catch {
    return undefined;
  }
};
